import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { BookOpen, CheckCircle2, Circle, FolderCog, Hourglass, Loader2, RotateCcw } from "lucide-react";
import { SelectionBar } from "@/components/SelectionBar";
import { SelectMenu, SelectMark, BarButton, LassoArea, useSelection } from "@/components/Selection";
import { removeWithPreview } from "@/components/SelectionActions";
import { ScanAudiobooksButton, ScanResults, useAudiobookScan } from "@/components/ScanAudiobooks";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Cover } from "@/components/Cover";
import { PageHeader, EmptyState } from "@/components/PageHeader";

const FILTERS = [["all", "All"], ["progress", "In progress"], ["new", "Not started"], ["done", "Finished"]];
export const fmtLong = (s) => { const h = Math.floor(s / 3600), m = Math.round((s % 3600) / 60); return h ? `${h} h ${m} min` : `${m} min`; };

const BookCard = ({ b, sel }) => {
  const pr = b.progress;
  const left = Math.max(0, (pr.total_duration || 0) - (pr.elapsed || 0));
  const on = sel?.has(b.id);
  return (
    <Link to={`/audiobooks/${b.id}`} data-testid={`book-card-${b.id}`} data-sel-id={b.id} aria-pressed={sel?.mode ? !!on : undefined}
      onClick={(e) => { if (sel?.mode) { e.preventDefault(); sel.toggle(b.id, e.shiftKey); } }}
      className={`group flex flex-col rounded-xl p-2 transition-colors hover:bg-white/60 ${on ? "bg-white ring-2 ring-zinc-900" : ""}`}>
      <div className="relative">
        <Cover albumId={b.id} hasCover={b.has_cover} version={b.cover_version} className={`aspect-square w-full shadow-md transition-transform duration-300 ${sel?.mode ? (on ? "scale-[0.96]" : "") : "group-hover:-translate-y-1"}`} rounded="rounded-lg" iconSize={36} />
        {sel?.mode && <SelectMark on={on} className="left-2 bottom-2" />}
        {pr.finished && <span className="absolute right-2 top-2 flex items-center gap-1 rounded-full bg-emerald-600 px-2 py-0.5 text-[10px] font-semibold text-white" data-testid={`book-finished-${b.id}`}><CheckCircle2 size={11} />Finished</span>}
        {!pr.finished && pr.pct > 0 && <span className="absolute right-2 top-2 rounded-full bg-zinc-900/80 px-2 py-0.5 text-[10px] font-semibold text-white">{Math.round(pr.pct)}%</span>}
        {b.protected && <span className="absolute left-2 top-2 rounded-full bg-rose-600 px-2 py-0.5 text-[10px] font-semibold text-white" title="Copy-protected (Audible / iTunes) — can't be played here" data-testid={`book-protected-${b.id}`}>Protected</span>}
      </div>
      <div className="mt-3 h-1 w-full overflow-hidden rounded-full bg-zinc-200" data-testid={`book-progress-${b.id}`}>
        <div className={`h-full ${pr.finished ? "bg-emerald-500" : "bg-amber-500"}`} style={{ width: `${pr.finished ? 100 : pr.pct}%` }} />
      </div>
      <p className="mt-2 truncate text-sm font-medium text-zinc-900">{b.name}</p>
      <p className="truncate text-xs text-zinc-500">{b.artist}</p>
      <p className="mt-0.5 text-[11px] text-zinc-400">{pr.finished ? fmtLong(pr.total_duration) : pr.pct > 0 ? `${fmtLong(left)} left` : `${fmtLong(pr.total_duration)} · ${b.chapter_count} chapters`}</p>
    </Link>
  );
};

export default function Audiobooks() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");
  const [params, setParams] = useSearchParams();
  const urlQ = (params.get("q") || "").toLowerCase();

  const load = useCallback((rescan) => api.get("/audiobooks", { params: rescan ? { rescan: 1 } : {} }).then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e))), []);
  useEffect(() => { load(false); }, [load]);
  const scan = useAudiobookScan(() => load(true));
  const items = useMemo(() => (data?.items || []).filter((b) => !urlQ || `${b.name} ${b.artist} ${b.genre || ""}`.toLowerCase().includes(urlQ)).filter((b) => filter === "all" || (filter === "done" ? b.progress.finished : filter === "progress" ? !b.progress.finished && b.progress.pct > 0 : !b.progress.finished && b.progress.pct === 0)), [data, filter, urlQ]);
  const ids = useMemo(() => items.map((b) => b.id), [items]);
  const s = useSelection(ids);
  const [busy, setBusy] = useState(false);
  const selected = [...s.sel];
  const bulk = async (action) => {
    if (action === "reset" && !window.confirm(`Reset listening progress for ${s.count} book${s.count === 1 ? "" : "s"}? Bookmarks are kept.`)) return;
    setBusy(true);
    try {
      const { data: r } = await api.post("/audiobooks/bulk", { ids: selected, action });
      toast.success(action === "finish" ? `Marked ${r.count} book${r.count === 1 ? "" : "s"} finished` : `Reset ${r.count} book${r.count === 1 ? "" : "s"}`);
      s.exit(); await load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  const removeSelected = async () => {
    setBusy(true);
    try {
      const n = s.count;
      const res = await removeWithPreview({ album_ids: selected }, `${n} audiobook${n === 1 ? "" : "s"}`);
      if (res) { s.exit(); await load(); }
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  const picks = [
    { label: "Select finished", icon: CheckCircle2, testid: "finished", count: items.filter((b) => b.progress.finished).length, onClick: () => s.where((b) => b.progress.finished, items) },
    { label: "Select in progress", icon: Hourglass, testid: "in-progress", count: items.filter((b) => !b.progress.finished && b.progress.pct > 0).length, onClick: () => s.where((b) => !b.progress.finished && b.progress.pct > 0, items) },
    { label: "Select not started", icon: Circle, testid: "not-started", count: items.filter((b) => !b.progress.finished && b.progress.pct === 0).length, onClick: () => s.where((b) => !b.progress.finished && b.progress.pct === 0, items) },
  ];
  return (
    <div className="animate-rise" data-testid="audiobooks-page">
      <PageHeader stack eyebrow={data ? `${data.total} book${data.total === 1 ? "" : "s"}` : "Audiobooks"} title="Audiobooks" testid="audiobooks-header">
        {urlQ && <button onClick={() => setParams({})} className="flex h-9 items-center gap-1.5 rounded-full border border-zinc-900 bg-zinc-900 px-4 text-xs text-white" data-testid="audiobooks-q-clear">“{params.get("q")}” ×</button>}
        <ScanAudiobooksButton scan={scan} />
        <Link to="/settings?page=filejump#audiobooks" data-testid="audiobooks-folders-link" className="flex h-9 items-center gap-2 rounded-full border border-zinc-300 bg-white px-4 text-xs font-medium hover:border-zinc-900"><FolderCog size={13} />Folders</Link>
        {data?.total > 0 && <SelectMenu s={s} noun="book" testid="audiobooks-select" picks={picks} size="h-9 text-xs" />}
      </PageHeader>
      <SelectionBar count={s.count} hidden={s.hidden} noun="book" busy={busy} onRemove={removeSelected} onClear={s.none} onSelectAll={!s.allShown ? s.all : null}>
        <BarButton onClick={() => bulk("finish")} icon={CheckCircle2} testid="selection-mark-finished" disabled={busy}>Mark finished</BarButton>
        <BarButton onClick={() => bulk("reset")} icon={RotateCcw} testid="selection-reset-progress" disabled={busy}>Reset progress</BarButton>
      </SelectionBar>
      <ScanResults scan={scan} />
      <div className="mb-6 flex flex-wrap gap-2" data-testid="audiobooks-filters">
        {FILTERS.map(([k, l]) => (
          <button key={k} onClick={() => setFilter(k)} data-testid={`audiobooks-filter-${k}`} className={`rounded-full px-3 py-1 text-xs transition-colors ${filter === k ? "bg-zinc-900 text-white" : "border border-zinc-300 bg-white text-zinc-700 hover:border-zinc-900"}`}>{l}</button>
        ))}
      </div>
      {!data ? <Loader2 className="animate-spin text-zinc-400" /> : items.length === 0 ? (
        <EmptyState icon={BookOpen} title={data.total ? "Nothing here" : "No audiobooks yet"}
          text={data.total ? "Try another filter." : "Books are found from an Audiobook/Spoken genre tag, .m4b files, or FileJump folders you choose in Settings. You can also mark any album as an audiobook from its page."} />
      ) : (
        <LassoArea s={s} className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6" testid="audiobooks-grid">
          {items.map((b) => <BookCard key={b.id} b={b} sel={s} />)}
        </LassoArea>
      )}
    </div>
  );
}
